import { ParsedUrlQuery } from 'querystring';
import { ComponentType, useEffect, useCallback, useState } from 'react';
import { NextComponentType, NextPageContext } from 'next';
import { useRouter } from 'next/router';
import { NamespacePath } from '@ads-bread/shared/bread/util';
import { getStateValueFromRoute } from '../../components/RouteMachineService';
import { useNamespace } from './useNamespace';

/**
 * Use the Routes enum from a route machine e.g. (ecommRoutingMachine, inStoreRoutingMachine, etc.)
 * to whitelist a route for Adobe Test and Target
 */
export const AdobeTargetConfig = {
  TEST_TARGET_URL_WHITELIST: [] as string[],
  PAGE_LOAD_DELAY: 3000,
};

interface WhitelistedUrlProps {
  url?: string;
  namespace: NamespacePath;
  query: ParsedUrlQuery;
}
/**
 * Determines if a URL is whitelisted for Adobe Target
 * @param WhitelistedUrlProps
 * @returns boolean
 */
const isWhitelistedUrl = ({
  url,
  namespace,
  query,
}: WhitelistedUrlProps): boolean => {
  if (!url) {
    return false;
  }

  const statePath = getStateValueFromRoute(url, namespace, query);
  return AdobeTargetConfig.TEST_TARGET_URL_WHITELIST.includes(statePath);
};

/**
 * Wraps the Next page component in order to hide content sections until ready.
 * The original snippet documentation is located at
 * https://experienceleague.adobe.com/en/docs/platform-learn/implement-in-websites/implement-solutions/target#add-the-target-pre-hiding-snippet
 * This is a reproduction for finer grained control over actual test target routes instead
 * of delaying load on every page.
 *
 * @param Component  NextComponentType<NextPageContext, unknown, P>
 * @returns  ComponentType<P>
 */
export const useAdobeTargetEnhancedComponent = <
  P extends Record<string, unknown>
>(
  Component: NextComponentType<NextPageContext, unknown, P>
): ComponentType<P> => {
  const router = useRouter();
  const { query } = router;
  const namespace = useNamespace();
  const [isPageReady, setIsPageReady] = useState(true);

  /**
   * Handles page change start and hides content
   * @params url string
   */
  const handleRouteChangeStart = useCallback((url: string) => {
    if (!isWhitelistedUrl({ url, namespace, query })) {
      return;
    }
    setIsPageReady(false);
  }, []);

  /**
   * Handles page change complete and shows content after PAGE_LOAD_DELAY
   * @params url string
   */
  const handleRouteChangeComplete = useCallback((url: string) => {
    if (!isWhitelistedUrl({ url, namespace, query })) {
      return;
    }
    setTimeout(() => {
      setIsPageReady(true);
    }, AdobeTargetConfig.PAGE_LOAD_DELAY);
  }, []);

  /**
   * Handles page change error and shows content immediately
   */
  const handleRouteChangeError = useCallback(() => {
    setIsPageReady(true);
  }, []);

  useEffect(() => {
    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeError);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeError', handleRouteChangeError);
    };
  }, [
    router.events,
    handleRouteChangeStart,
    handleRouteChangeComplete,
    handleRouteChangeError,
  ]);

  const WrappedComponent: ComponentType<P> = (props: P) => {
    return (
      <div
        id="content-wrapper"
        data-testid="content-wrapper"
        className={`${isPageReady ? 'opacity-100' : 'opacity-0'}`}
      >
        <Component {...props} />
      </div>
    );
  };

  return WrappedComponent;
};
